<script setup>
/**
 * Catalog View
 * Product catalog with departments + subdepartments menu and items grid
 * Uses CatalogStore for state management and caching
 */
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '../../app/store/auth.js'
import { useCatalogStore } from '../../app/store/catalog.js'
import { useAssortmentRequestStore } from '../../app/store/assortmentRequest.js'
import { ROUTES } from '../../core/config/constants.js'
import { useSwipeNavigation } from '../../core/composables/useSwipeNavigation.js'
import { getPromoMakats } from '../../core/utils/promoCalc.js'
import CatalogLoader from '../components/catalog/CatalogLoader.vue'
import DepartmentsMenu from '../components/catalog/DepartmentsMenu.vue'
import CatalogItemCard from '../components/catalog/CatalogItemCard.vue'
import PromotionCard from '../components/catalog/PromotionCard.vue'
import CatalogFilters from '../components/catalog/CatalogFilters.vue'
import ItemDialog from '../components/catalog/item-dialog/ItemDialog.vue'
import MobileSubcategoryBar from '../components/catalog/MobileSubcategoryBar.vue'
import DesktopCategoryStrip from '../components/catalog/DesktopCategoryStrip.vue'
import SwipeTutorial from '../components/catalog/SwipeTutorial.vue'
import MobileFiltersSheet from '../components/catalog/MobileFiltersSheet.vue'
import MarketingConsentModal from '../components/catalog/MarketingConsentModal.vue'
import SiteTermsModal from '../components/catalog/SiteTermsModal.vue'
import { hasAcceptedSiteTerms, hasMarketingAnswer } from '@core/utils/consent.js'
import ClientSelectionModal from '../components/catalog/ClientSelectionModal.vue'
import ImmersiveSwitchButton from '../components/catalog/ImmersiveSwitchButton.vue'

// ============================================
// Stores
// ============================================
const router = useRouter()
const authStore = useAuthStore()
const catalogStore = useCatalogStore()
const assortmentRequestStore = useAssortmentRequestStore()
const { count: niaSelectionCount, isEmpty: niaSelectionEmpty } = storeToRefs(assortmentRequestStore)

// Destructure reactive refs from catalog store
const {
  isBootstrapping,
  bootstrapError,
  departments,
  departmentsWithPromo,
  visibleDepartments,
  visibleCurrentDeptSubs,
  visibleSubsByDeptIdObject,
  departmentsLoading,
  currentDeptSubs,
  subsByDepartmentId,
  subsByDeptIdObject,
  subsLoading,
  selectedDepartmentId,
  selectedDepartmentName,
  selectedSubDepartmentId,
  selectedSubDepartmentName,
  searchQuery,
  isSearchActive,
  searchResults,
  visibleItems,
  showSearchHelper,
  itemsArray,
  clientMissingMessage,
  catalogMode,
  linkedClient,
  activeFilterCount,
  singlePromoItems,
  multiItemPromotions,
  firstOrderMultiPromos,
  regularMultiPromos,
  firstOrderSinglePromoItems,
  regularSinglePromoItems,
  isMobileView,
  isTabletView,
  isPCView,
  validItemsPerRow,
  isGridMode,
  gridStyle,
} = storeToRefs(catalogStore)

// Aliases for template compatibility
const isMobile = isMobileView
const isTablet = isTabletView
const isPC = isPCView

// Get the promo department ID constant
const PROMO_DEPARTMENT_ID = catalogStore.PROMO_DEPARTMENT_ID

// ============================================
// Local UI State
// ============================================
const isMobileDepartmentsOpen = ref(false)
const desktopSidebarCollapsed = ref(false)
const menuViewMode = ref('departments')
const departmentSearchQuery = ref('')
const productsContainerRef = ref(null)
const searchOpenMobile = ref(false)
const filtersOpenMobile = ref(false)
const clientMissingPopupDismissed = ref(false)
const showMarketingConsent = ref(false)
const showSiteTerms = ref(false)
const pendingAutoSelectDept = ref(null)

// Sub-client selection (full-screen picker)
const showClientSelection = ref(false)
const clientSelectionCandidates = computed(() => {
  const p = authStore.user?.profile
  if (authStore.userRole !== 'client' || !p) return []
  const subs = Array.isArray(p.sub_clients) ? p.sub_clients : []
  if (subs.length === 0) return []
  const list = []
  if (p.client_makat) {
    list.push({ makat: String(p.client_makat), name: String(p.client_name || '') })
  }
  for (const sc of subs) {
    if (sc?.makat) list.push({ makat: String(sc.makat), name: String(sc.name || '') })
  }
  const seen = new Set()
  return list.filter((c) => {
    if (seen.has(c.makat)) return false
    seen.add(c.makat)
    return true
  })
})

// Computed: show client missing popup
const showClientMissingPopup = computed(() => {
  return !!clientMissingMessage.value && !clientMissingPopupDismissed.value
})

// Computed: show prices (hide for guests and if client.prices === false)
const showPrices = computed(() => {
  if (!authStore.isAuthenticated) return false
  const client = authStore.user.client
  if (client && client.prices === false) return false
  return true
})

// Item Dialog state
const isItemDialogOpen = ref(false)
const selectedMakat = ref(null)

function navigateNotInAssortment(useReplace = false) {
  const method = useReplace ? 'replace' : 'push'
  router[method]({ name: ROUTES.NOT_IN_ASSORTMENT })
}

function navigateToAssortmentRequest() {
  router.push({ name: ROUTES.ASSORTMENT_REQUEST })
}

function openItemDialog(makat) {
  // Block dialog for not-in-assortment items
  const item = catalogStore.itemsByMakat.get(makat)
  if (!item) {
    // Check not-in-assortment map
    const niaItem = catalogStore.allDisplayableItems.find((i) => i.makat === makat)
    if (niaItem?._notInAssortment) return
  }

  // Outside promotions dept: if item belongs to a grouped promotion, navigate to it
  if (selectedDepartmentId.value !== PROMO_DEPARTMENT_ID) {
    const groupedPromo = multiItemPromotions.value.find((p) => getPromoMakats(p).includes(makat))
    if (groupedPromo) {
      router.push({ name: ROUTES.PROMOTION_DETAIL, params: { promoId: groupedPromo.id } })
      return
    }
  }

  selectedMakat.value = makat
  isItemDialogOpen.value = true
}

// ============================================
// Swipe Navigation
// ============================================
const swipeEnabled = computed(
  () => !isSearchActive.value && !isBootstrapping.value && !isItemDialogOpen.value
)

const { swipeStyle, transitionLabel } = useSwipeNavigation({
  containerRef: productsContainerRef,
  departments: visibleDepartments,
  getSubsForDept: (deptId) => catalogStore.getVisibleSubsForDept(deptId),
  selectedDepartmentId,
  selectedSubDepartmentId,
  promoDepartmentId: PROMO_DEPARTMENT_ID,
  onNavigate: (subId, subName, deptId, deptName) => {
    catalogStore.selectSubDepartment(subId, subName, deptId, deptName)
    menuViewMode.value = 'subs'
    if (productsContainerRef.value) {
      productsContainerRef.value.scrollTo({ top: 0, behavior: 'instant' })
    }
  },
  enabled: swipeEnabled,
  loadAllSubs: () => catalogStore.loadAllSubs(),
})

// Check if promo department has any items (multi-item promos OR single-item promo items)
const isPromoDeptEmpty = computed(() => {
  if (selectedDepartmentId.value !== PROMO_DEPARTMENT_ID) return false
  return multiItemPromotions.value.length === 0 && singlePromoItems.value.length === 0
})

// ============================================
// UI Actions
// ============================================
function scrollToTop() {
  if (productsContainerRef.value) {
    productsContainerRef.value.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }
}

function toggleMobileDepartments() {
  isMobileDepartmentsOpen.value = !isMobileDepartmentsOpen.value
}

function toggleDesktopSidebar() {
  desktopSidebarCollapsed.value = !desktopSidebarCollapsed.value
}

function toggleMobileSearch() {
  searchOpenMobile.value = !searchOpenMobile.value
  if (searchOpenMobile.value) {
    filtersOpenMobile.value = false
  }
}

function closeMobileSearch() {
  searchOpenMobile.value = false
}

function toggleMobileFilters() {
  filtersOpenMobile.value = !filtersOpenMobile.value
  if (filtersOpenMobile.value) {
    searchOpenMobile.value = false
  }
}

function closeMobileFilters() {
  filtersOpenMobile.value = false
}

function dismissMobileOverlays(e) {
  if (searchOpenMobile.value) {
    e.stopPropagation()
    closeMobileSearch()
  }
}

function dismissClientMissingPopup() {
  clientMissingPopupDismissed.value = true
  catalogStore.clearClientMissingMessage()
}

// ============================================
// Catalog Actions (delegated to store)
// ============================================
function selectDepartment({ id, name }) {
  catalogStore.selectDepartment(id, name)
  // Skip subs view for promo department (it has no subdepartments)
  if (id !== PROMO_DEPARTMENT_ID) {
    menuViewMode.value = 'subs'
    pendingAutoSelectDept.value = id
  } else {
    // Stay in departments view for promo dept, close mobile menu if open
    menuViewMode.value = 'departments'
    pendingAutoSelectDept.value = null
    if (isMobileDepartmentsOpen.value) {
      isMobileDepartmentsOpen.value = false
    }
  }
  scrollToTop()
}

// Auto-select first subcategory when department subs finish loading
watch(
  () => [visibleCurrentDeptSubs.value, subsLoading.value],
  ([subs, loading]) => {
    if (pendingAutoSelectDept.value && !loading && subs.length > 0) {
      const deptId = pendingAutoSelectDept.value
      const dept = visibleDepartments.value.find((d) => d.id === deptId)
      catalogStore.selectSubDepartment(subs[0].id, subs[0].name, deptId, dept?.name)
      pendingAutoSelectDept.value = null
    }
  }
)

function selectSubDepartment({ id, name, departmentId, departmentName }) {
  catalogStore.selectSubDepartment(id, name, departmentId, departmentName)

  if (isMobileDepartmentsOpen.value) {
    isMobileDepartmentsOpen.value = false
  }
  scrollToTop()
}

function goBackToDepartments() {
  menuViewMode.value = 'departments'
}

function handleSearchInput(event) {
  catalogStore.setSearchQuery(event.target.value)
}

function clearSearch() {
  catalogStore.clearSearch()
  searchOpenMobile.value = false
}

function handleSearchUpdate(query) {
  departmentSearchQuery.value = query
}

function handleRequestSubsForSearch() {
  catalogStore.loadAllSubs()
}

// ============================================
// Watchers
// ============================================
watch(
  [selectedDepartmentId, selectedSubDepartmentId],
  () => {
    // Items visibility tracking
  },
  { immediate: false }
)

watch(isSearchActive, (active) => {
  if (active) {
    scrollToTop()
  }
})

// Consent popups — once per catalog load, for client users only, in this order:
//   1. Site terms (תקנון) until `siteTerms.accepted === true` (dismissible, re-asked next visit)
//   2. Marketing until a real Yes/No is recorded (`marketing.answered === true`)
// Both are skipped when already answered. See src/core/utils/consent.js.
function isConsentEligibleClient() {
  const client = authStore.user?.client
  return authStore.userRole === 'client' && !!client && !clientMissingMessage.value
}

function maybeShowMarketingConsent() {
  if (showMarketingConsent.value) return
  if (isConsentEligibleClient() && !hasMarketingAnswer(authStore.user.client)) {
    showMarketingConsent.value = true
  }
}

function onSiteTermsClosed() {
  showSiteTerms.value = false
  maybeShowMarketingConsent()
}

watch(isBootstrapping, (loading) => {
  if (loading || showSiteTerms.value || showMarketingConsent.value) return
  if (!isConsentEligibleClient()) return
  if (!hasAcceptedSiteTerms(authStore.user.client)) {
    showSiteTerms.value = true
    return
  }
  maybeShowMarketingConsent()
})

// ============================================
// Lifecycle
// ============================================
function startBootstrap() {
  const userProfile = authStore.isAuthenticated ? authStore.user.profile : null
  catalogStore.bootstrap(userProfile).catch((err) => {
    console.error('[CatalogView] Bootstrap failed:', err)
  })
}

async function onClientSelected(client) {
  // First-time selection on catalog entry — cart hasn't been restored yet,
  // so no confirmation needed (cart restore happens during bootstrap).
  await authStore.setActiveClient(client.makat, client.name)
  showClientSelection.value = false
  startBootstrap()
}

onMounted(() => {
  // For 'client' users with sub_clients we must prompt for a working
  // client BEFORE the catalog bootstraps, so the right assortment /
  // prices are fetched. Guests, agents, admins, and clients without
  // sub_clients fall through to the original behavior.
  const needsPicker =
    authStore.userRole === 'client' &&
    !authStore.activeClientSelected &&
    clientSelectionCandidates.value.length > 1

  if (needsPicker) {
    showClientSelection.value = true
    return
  }

  startBootstrap()
})
</script>

<template>
  <div class="catalog-page">
    <!-- Client Missing Popup Modal -->
    <Teleport to="body">
      <div
        v-if="showClientMissingPopup"
        class="client-missing-overlay"
        @click.self="dismissClientMissingPopup"
      >
        <div class="client-missing-modal">
          <div class="client-missing-icon">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <p class="client-missing-message">{{ clientMissingMessage }}</p>
          <button class="client-missing-dismiss-btn" @click="dismissClientMissingPopup">
            הבנתי
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Consent Modals: site terms first, then marketing -->
    <SiteTermsModal :open="showSiteTerms" @close="onSiteTermsClosed" />
    <MarketingConsentModal :open="showMarketingConsent" @close="showMarketingConsent = false" />

    <!-- Sub-Client Selection Modal (full-screen, cannot be dismissed) -->
    <ClientSelectionModal
      :open="showClientSelection"
      :main-makat="authStore.mainClientMakat"
      :candidates="clientSelectionCandidates"
      @selected="onClientSelected"
    />

    <!-- Page Header -->
    <header class="catalog-header">
      <div class="header-content">
        <div class="header-main">
          <h1 class="page-title">קטלוג</h1>
          <!-- Selected Department/Sub Badge (Desktop) -->
          <div v-if="selectedDepartmentName" class="selected-badge desktop-only">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
            </svg>
            <span>{{ selectedDepartmentName }}</span>
            <template v-if="selectedSubDepartmentName">
              <span class="badge-separator">/</span>
              <span>{{ selectedSubDepartmentName }}</span>
            </template>
          </div>
        </div>

        <div class="header-actions">
          <!-- Quick switch to immersive (desktop header; mobile/tablet use the cart footer).
               Gated by the catalog's own desktop breakpoint (`desktop-only`, >=1280) so it never
               sits beside the mobile header buttons; the 1024-1279 band reaches immersive via the
               side-menu chooser. -->
          <div
            v-tutorial="{
              id: 'catalog-desktop-immersive',
              text: 'רוצים חוויית קנייה מהירה? כפתור זה מעביר לקטלוג האימרסיבי — מוצר אחרי מוצר במסך מלא',
              importance: 'info',
              showOnce: true,
              autoTrigger: false,
              group: 'catalog-desktop-intro',
              step: 4,
            }"
            class="desktop-only"
          >
            <ImmersiveSwitchButton variant="header" />
          </div>

          <!-- Mobile: Departments Button -->
          <button
            v-tutorial="{
              id: 'catalog-mobile-departments',
              text: 'כאן בוחרים מחלקה ותת־מחלקה — כל המוצרים מסודרים לפי קטגוריות',
              importance: 'important',
              showOnce: true,
              autoTrigger: true,
              group: 'catalog-mobile-intro',
              step: 1,
            }"
            class="departments-btn mobile-only"
            @click="toggleMobileDepartments"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
            </svg>
            <span>מחלקות</span>
          </button>

          <!-- Mobile Search Button -->
          <button
            v-tutorial="{
              id: 'catalog-mobile-search',
              text: 'חיפוש מהיר בכל הקטלוג — לפי שם מוצר, מק״ט או ברקוד',
              importance: 'info',
              showOnce: true,
              autoTrigger: false,
              group: 'catalog-mobile-intro',
              step: 2,
            }"
            class="departments-btn mobile-only"
            @click="toggleMobileSearch"
            :class="{ active: isSearchActive }"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <span>חיפוש</span>
            <span v-if="isSearchActive" class="search-count">{{ searchResults.length }}</span>
          </button>

          <!-- Mobile Filter Button -->
          <button
            v-tutorial="{
              id: 'catalog-mobile-filters',
              text: 'סינון מוצרים לפי כשרות וזמינות במלאי — כדי לראות רק מה שרלוונטי לכם',
              importance: 'info',
              showOnce: true,
              autoTrigger: false,
              group: 'catalog-mobile-intro',
              step: 3,
            }"
            class="departments-btn mobile-only"
            @click="toggleMobileFilters"
            :class="{ active: activeFilterCount > 0 }"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
            </svg>
            <span>מסננים</span>
          </button>
        </div>
      </div>

      <!-- Mobile Nav Row: scrollers -->
      <div class="mobile-nav-row mobile-only">
        <MobileSubcategoryBar
          :selected-department-id="selectedDepartmentId"
          :selected-sub-department-id="selectedSubDepartmentId"
          :sub-departments="visibleCurrentDeptSubs"
          :departments="visibleDepartments"
          :promo-department-id="PROMO_DEPARTMENT_ID"
          :subs-loading="subsLoading"
          @select-subdepartment="selectSubDepartment"
          @select-department="selectDepartment"
        />
      </div>

      <!-- Mobile Search Overlay -->
      <div v-if="searchOpenMobile" class="mobile-search-overlay mobile-only">
        <div class="mobile-search-container">
          <div class="mobile-search-input-wrapper">
            <svg
              class="search-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              class="mobile-search-input"
              placeholder="חיפוש לפי שם, מק״ט או ברקוד..."
              :value="searchQuery"
              @input="handleSearchInput"
              autofocus
            />
            <button v-if="searchQuery" class="search-clear-btn" @click="clearSearch">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p v-if="showSearchHelper" class="search-helper">הקלד לפחות 3 תווים</p>
          <p v-else-if="isSearchActive" class="search-results-count">
            נמצאו {{ searchResults.length }} פריטים
          </p>
        </div>
        <!-- Collapse Button -->
        <button class="mobile-search-collapse-btn" @click="closeMobileSearch">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
      </div>
    </header>

    <MobileFiltersSheet
      v-model="filtersOpenMobile"
      @navigate-not-in-assortment="navigateNotInAssortment"
    />

    <!-- Desktop Category Strip: shown only when sidebar is collapsed on PC -->
    <DesktopCategoryStrip
      v-if="desktopSidebarCollapsed"
      :selected-department-id="selectedDepartmentId"
      :selected-sub-department-id="selectedSubDepartmentId"
      :sub-departments="visibleCurrentDeptSubs"
      :departments="visibleDepartments"
      :promo-department-id="PROMO_DEPARTMENT_ID"
      :subs-loading="subsLoading"
      @select-department="selectDepartment"
      @select-subdepartment="selectSubDepartment"
    />

    <!-- Main Layout: Sidebar + Content -->
    <div class="catalog-layout">
      <!-- Desktop Sidebar with Search + Menu -->
      <aside class="catalog-sidebar desktop-only" :class="{ collapsed: desktopSidebarCollapsed }">
        <!-- Collapsed State: Expand Button -->
        <div v-if="desktopSidebarCollapsed" class="sidebar-collapsed-content">
          <button class="sidebar-expand-btn" @click="toggleDesktopSidebar" title="הרחב תפריט">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div class="collapsed-label">מחלקות</div>
        </div>

        <!-- Expanded State: Search + Menu -->
        <template v-else>
          <!-- Desktop Search Row -->
          <div class="desktop-search">
            <div class="desktop-search-row">
              <div
                v-tutorial="{
                  id: 'catalog-desktop-search',
                  text: 'חיפוש מהיר בכל הקטלוג — לפי שם מוצר, מק״ט או ברקוד',
                  importance: 'info',
                  showOnce: true,
                  autoTrigger: true,
                  group: 'catalog-desktop-intro',
                  step: 1,
                }"
                class="desktop-search-input-wrapper"
              >
                <svg
                  class="search-icon"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  class="desktop-search-input"
                  placeholder="חיפוש פריט..."
                  :value="searchQuery"
                  @input="handleSearchInput"
                />
                <button v-if="searchQuery" class="search-clear-btn" @click="clearSearch">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <!-- Collapse Button -->
              <button class="sidebar-collapse-btn" @click="toggleDesktopSidebar" title="צמצם תפריט">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
            <p v-if="showSearchHelper" class="search-helper">הקלד לפחות 3 תווים</p>
            <p v-else-if="isSearchActive" class="search-results-count">
              נמצאו {{ searchResults.length }} פריטים
            </p>
          </div>

          <!-- Desktop Filters -->
          <CatalogFilters show-tutorial @navigate-not-in-assortment="navigateNotInAssortment" />

          <!-- Departments Menu Component -->
          <DepartmentsMenu
            show-tutorial
            :departments="visibleDepartments"
            :sub-departments="visibleCurrentDeptSubs"
            :subs-by-department-id="visibleSubsByDeptIdObject"
            :catalog-items="itemsArray"
            :selected-department-id="selectedDepartmentId"
            :selected-department-name="selectedDepartmentName"
            :selected-sub-department-id="selectedSubDepartmentId"
            :selected-sub-department-name="selectedSubDepartmentName"
            :view-mode="menuViewMode"
            :is-mobile-open="isMobileDepartmentsOpen"
            :is-desktop-collapsed="desktopSidebarCollapsed"
            :is-loading="departmentsLoading"
            :subs-loading="subsLoading"
            @select-department="selectDepartment"
            @select-subdepartment="selectSubDepartment"
            @go-back="goBackToDepartments"
            @toggle-mobile="toggleMobileDepartments"
            @toggle-desktop="toggleDesktopSidebar"
            @update-search="handleSearchUpdate"
            @request-subs-for-search="handleRequestSubsForSearch"
          />
        </template>
      </aside>

      <!-- Products Container -->
      <main
        ref="productsContainerRef"
        class="products-container"
        :class="{ 'no-padding': isGridMode }"
      >
        <!-- Visitor CTA Banner (sticky, for unauthenticated users only) -->
        <div v-if="!authStore.isAuthenticated" class="visitor-cta-banner">
          <div class="visitor-cta-content">
            <div class="visitor-cta-text-group">
              <span class="visitor-cta-text">לצפייה במחירים והתחברות</span>
              <span class="visitor-cta-subtitle">הצטרפו לאלפי קמעונאים</span>
            </div>
            <RouterLink :to="{ name: ROUTES.JOIN_NETWORK }" class="visitor-cta-button">
              לחץ כאן
            </RouterLink>
          </div>
        </div>

        <!-- Loading State -->
        <CatalogLoader v-if="isBootstrapping" />

        <!-- Error State -->
        <div v-else-if="bootstrapError" class="error-section">
          <div class="error-icon">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h2>שגיאה בטעינה</h2>
          <p class="text-muted">{{ bootstrapError }}</p>
          <button class="btn-retry" @click="catalogStore.bootstrap(authStore.user.profile)">
            נסה שוב
          </button>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="
            isPromoDeptEmpty ||
            (selectedDepartmentId !== PROMO_DEPARTMENT_ID && visibleItems.length === 0)
          "
          class="empty-section"
        >
          <div class="empty-icon">
            <svg
              v-if="isSearchActive"
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <p class="empty-text">
            {{
              isSearchActive
                ? 'לא נמצאו פריטים תואמים'
                : selectedDepartmentId === PROMO_DEPARTMENT_ID
                  ? 'אין פריטים במבצעים כרגע'
                  : activeFilterCount > 0
                    ? 'אין פריטים זמינים במחלקה זו, ייתכן שחלק מהסננים הסירו את כלל הפריטים מהמחלקה.'
                    : 'אין פריטים במחלקה זו'
            }}
          </p>
        </div>

        <!-- Swipe Container (touch navigation between sub-departments) -->
        <div v-else class="swipe-container" :style="swipeStyle">
          <div
            class="items-grid"
            :class="{ 'grid-mode': isGridMode }"
            :style="gridStyle"
            @click.capture="dismissMobileOverlays"
          >
            <!-- Promotions Department: First_order promos first, then regular -->
            <template v-if="selectedDepartmentId === PROMO_DEPARTMENT_ID && !isSearchActive">
              <!-- First_order multi-item promos -->
              <PromotionCard
                v-for="promo in firstOrderMultiPromos"
                :key="'promo-' + promo.id"
                :promotion="promo"
                :items-per-row="validItemsPerRow"
                :is-mobile="isMobile"
                :is-tablet="isTablet"
                :is-grid-mode="isGridMode"
              />
              <!-- First_order single-item promos -->
              <CatalogItemCard
                v-for="item in firstOrderSinglePromoItems"
                :key="item.makat"
                :item="item"
                :items-per-row="validItemsPerRow"
                :is-mobile="isMobile"
                :is-tablet="isTablet"
                :is-grid-mode="isGridMode"
                :show-prices="showPrices"
                @click="openItemDialog"
              />
              <!-- Regular multi-item promos -->
              <PromotionCard
                v-for="promo in regularMultiPromos"
                :key="'promo-' + promo.id"
                :promotion="promo"
                :items-per-row="validItemsPerRow"
                :is-mobile="isMobile"
                :is-tablet="isTablet"
                :is-grid-mode="isGridMode"
              />
              <!-- Regular single-item promos -->
              <CatalogItemCard
                v-for="item in regularSinglePromoItems"
                :key="item.makat"
                :item="item"
                :items-per-row="validItemsPerRow"
                :is-mobile="isMobile"
                :is-tablet="isTablet"
                :is-grid-mode="isGridMode"
                :show-prices="showPrices"
                @click="openItemDialog"
              />
            </template>
            <!-- Regular departments or search results -->
            <template v-else>
              <CatalogItemCard
                v-for="(item, index) in visibleItems"
                :key="item.makat"
                :item="item"
                :items-per-row="validItemsPerRow"
                :is-mobile="isMobile"
                :is-tablet="isTablet"
                :is-grid-mode="isGridMode"
                :show-prices="showPrices"
                :tutorial-config="
                  index === 0
                    ? {
                        id: isPC ? 'catalog-desktop-product' : 'catalog-mobile-product',
                        text: 'לחצו על מוצר לצפייה בפרטים ולהוספה לעגלה',
                        importance: 'important',
                        showOnce: true,
                        autoTrigger: false,
                        group: isPC ? 'catalog-desktop-intro' : 'catalog-mobile-intro',
                        step: isPC ? 4 : 4,
                      }
                    : null
                "
                @click="openItemDialog"
              />
            </template>
          </div>
        </div>

        <!-- Swipe Tutorial (one-time hint for touch users) -->
        <SwipeTutorial v-if="!isSearchActive && !isBootstrapping" />

        <!-- Swipe transition label (absolutely positioned, visible when content is off-screen) -->
        <div v-if="transitionLabel" class="swipe-transition-label">
          <span class="swipe-transition-dept">{{ transitionLabel.deptName }}</span>
          <span class="swipe-transition-sub">{{ transitionLabel.subName }}</span>
          <svg
            class="swipe-transition-arrow"
            viewBox="0 0 24 24"
            width="28"
            height="28"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path v-if="transitionLabel.direction === 'next'" d="M19 12H5M12 5l-7 7 7 7" />
            <path v-else d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>
      </main>
    </div>

    <!-- Item Dialog -->
    <ItemDialog v-model:open="isItemDialogOpen" :makat="selectedMakat" />

    <!-- Assortment Request Floating CTA (desktop circle) -->
    <Transition name="fade-up">
      <button
        v-if="!niaSelectionEmpty"
        class="assortment-request-fab"
        @click="navigateToAssortmentRequest"
      >
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
        <span class="fab-count">{{ niaSelectionCount }}</span>
      </button>
    </Transition>
  </div>
</template>

<style scoped>
/* ============================================
   Page Layout
   ============================================ */
.catalog-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--header-height));
  overflow: hidden;
}

/* ============================================
   Header
   ============================================ */
.catalog-header {
  position: relative;
  flex-shrink: 0;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
}

.header-main {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.selected-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
}

.badge-separator {
  color: var(--text-muted);
  margin: 0 2px;
}

/* View Controls */
/* Mobile Nav Row: scrollers + action buttons side by side */
.mobile-nav-row {
  display: none;
  align-items: stretch;
}

/* Mobile Action Controls (search + filter) */
.mobile-action-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-around;
  flex-shrink: 0;
  padding: 0 4px;
  min-width: 78px;
}

/* Mobile Departments Button */
.departments-btn {
  display: none;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 8px 14px;
  background: var(--bg-secondary);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.departments-btn:hover {
  background: var(--bg-tertiary);
}

.departments-btn:active {
  background: var(--bg-tertiary);
}

.departments-btn.active {
  background: var(--bg-tertiary);
  color: var(--color-primary);
}

.btn-badge {
  padding: 2px 8px;
  background: var(--color-primary);
  border-radius: var(--border-radius-full);
  font-size: 11px;
  color: white;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============================================
   Desktop Sidebar & Search
   ============================================ */
.catalog-sidebar {
  display: flex;
  flex-direction: column;
  width: 280px;
  flex-shrink: 0;
  min-height: 0;
  background: var(--bg-primary);
  border-left: 1px solid var(--border-color);
  overflow: hidden;
  transition: width var(--transition-normal);
}

.catalog-sidebar.collapsed {
  width: 48px;
}

/* Sidebar Collapsed Content */
.sidebar-collapsed-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-sm);
  width: 100%;
}

.sidebar-expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sidebar-expand-btn:hover {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.collapsed-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  margin-top: var(--spacing-md);
  letter-spacing: 2px;
}

.desktop-search {
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
}

.desktop-search-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding-left: var(--spacing-xs);
}

.sidebar-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sidebar-collapse-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-color: var(--text-muted);
}

.desktop-search-input-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  height: 36px;
  padding: 0 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.desktop-search-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.desktop-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  outline: none;
  text-align: right;
}

.desktop-search-input::placeholder {
  color: var(--text-muted);
}

.search-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

.search-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.search-clear-btn:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.search-helper {
  margin: var(--spacing-xs) 0 0;
  font-size: 12px;
  color: var(--text-muted);
  text-align: right;
}

.search-results-count {
  margin: var(--spacing-xs) 0 0;
  font-size: 12px;
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
  text-align: right;
}

/* ============================================
   Mobile Search
   ============================================ */
.mobile-search-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  height: 22px;
  padding: 0 var(--spacing-sm);
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.mobile-search-btn:hover {
  color: var(--color-primary);
}

.mobile-search-btn.active {
  color: var(--color-primary);
}

.search-count {
  position: absolute;
  top: -6px;
  right: -6px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  border-radius: var(--border-radius-full);
  padding: 0 4px;
  line-height: 1;
}

/* Mobile Filter Button */
.mobile-filter-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  height: 20px;
  padding: 0 var(--spacing-sm);
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  white-space: nowrap;
}

.mobile-filter-btn:hover {
  color: var(--color-primary);
}

.mobile-filter-btn.active {
  color: var(--color-primary);
}

.filter-count {
  position: absolute;
  top: -6px;
  right: -6px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: var(--color-primary);
  border-radius: var(--border-radius-full);
  padding: 0 4px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.mobile-search-overlay {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-sm) var(--spacing-md);
  box-shadow: var(--shadow-md);
  z-index: 100;
  animation: slideDown 0.2s ease-out;
}

.mobile-search-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.mobile-search-collapse-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.mobile-search-collapse-btn:active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-search-container {
  flex: 1;
  min-width: 0;
}

.mobile-search-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  height: 42px;
  padding: 0 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.mobile-search-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.mobile-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-base);
  font-family: var(--font-family);
  color: var(--text-primary);
  outline: none;
  text-align: right;
}

.mobile-search-input::placeholder {
  color: var(--text-muted);
}

/* ============================================
   Main Layout
   ============================================ */
.catalog-layout {
  flex: 1;
  display: flex;
  flex-direction: row;
  min-height: 0;
  overflow: hidden;
}

/* ============================================
   Products Container
   ============================================ */
.products-container {
  position: relative;
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--spacing-lg);
  background: var(--bg-secondary);
  touch-action: pan-y;
}

.products-container.no-padding {
  padding: 0;
}

.swipe-container {
  min-height: 0;
}

.swipe-transition-label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
  z-index: 0;
}

.swipe-transition-dept {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.swipe-transition-sub {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.swipe-transition-arrow {
  margin-top: 6px;
  color: var(--text-muted);
}

/* Error State */
.error-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-2xl);
  height: 100%;
}

.error-icon {
  color: var(--color-error);
  margin-bottom: var(--spacing-md);
}

.error-section h2 {
  margin-bottom: var(--spacing-sm);
  color: var(--color-error);
}

.btn-retry {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-retry:hover {
  background: var(--color-primary-dark);
}

/* Empty State */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  height: 100%;
}

.empty-icon {
  color: var(--text-muted);
  margin-bottom: var(--spacing-md);
  opacity: 0.5;
}

.empty-text {
  font-size: var(--font-size-base);
  color: var(--text-muted);
  margin: 0;
}

/* Items Grid */
.items-grid {
  display: grid;
  gap: var(--spacing-md);
}

.items-grid.grid-mode {
  gap: 0;
  border-radius: 0;
  overflow: hidden;
}

/* ============================================
   Responsive
   Breakpoints: Mobile < 768px | Tablet 768-1279px | PC >= 1280px
   ============================================ */
.desktop-only {
  display: flex;
}

.mobile-only {
  display: none;
}

/* Tablet: 768px - 1279px */
@media (min-width: 768px) and (max-width: 1279px) {
  .catalog-header {
    padding: 4px 6px;
    border-bottom: none;
  }

  .page-title {
    font-size: var(--font-size-base);
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: flex;
  }

  .mobile-nav-row {
    display: flex;
    min-height: 60px;
  }

  .products-container {
    padding: var(--spacing-md);
  }

  .products-container.no-padding {
    padding: 0;
  }

  .catalog-layout {
    flex-direction: column;
  }

  .items-grid {
    gap: var(--spacing-md);
  }
}

/* Mobile: < 768px */
@media (max-width: 767px) {
  .catalog-header {
    padding: 4px 6px;
    border-bottom: none;
  }

  .page-title {
    font-size: var(--font-size-sm);
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: flex;
  }

  .mobile-nav-row {
    display: flex;
  }

  .products-container {
    padding: var(--spacing-sm);
  }

  .products-container.no-padding {
    padding: 0;
  }

  .catalog-layout {
    flex-direction: column;
  }

  .items-grid {
    gap: var(--spacing-sm);
  }
}

/* ============================================
   Client Missing Popup Modal
   ============================================ */
.client-missing-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-md);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.client-missing-modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: var(--shadow-xl);
  animation: scaleIn 0.2s ease-out;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.client-missing-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin: 0 auto var(--spacing-lg);
  background: rgba(245, 158, 11, 0.1);
  border-radius: var(--border-radius-full);
  color: #f59e0b;
}

.client-missing-message {
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: var(--text-primary);
  margin: 0 0 var(--spacing-lg);
}

.client-missing-dismiss-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-xl);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.client-missing-dismiss-btn:hover {
  background: var(--color-primary-dark);
}

.client-missing-dismiss-btn:active {
  transform: scale(0.98);
}

/* ============================================
   Visitor CTA Banner (unauthenticated users)
   Sticky at the top of the products scroll area.
   Design matches the home page sticky CTA banner.
   ============================================ */
.visitor-cta-banner {
  position: sticky;
  top: 0;
  z-index: 5;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 8px 16px;
}

.visitor-cta-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.visitor-cta-text-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
}

.visitor-cta-text {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.visitor-cta-subtitle {
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
}

.visitor-cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  background: var(--color-primary);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 20px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(30, 41, 59, 0.2);
  white-space: nowrap;
}

.visitor-cta-button:hover {
  background: var(--color-primary-hover);
  transform: scale(1.05);
  box-shadow: 0 4px 10px rgba(30, 41, 59, 0.3);
}

/* Tablet: 768px - 1279px */
@media (min-width: 768px) and (max-width: 1279px) {
  .visitor-cta-banner {
    padding: 10px 24px;
  }

  .visitor-cta-content {
    gap: 24px;
  }

  .visitor-cta-text {
    font-size: 16px;
  }

  .visitor-cta-button {
    padding: 10px 28px;
    font-size: 14px;
    border-radius: 24px;
  }
}

/* Desktop: >= 1280px */
@media (min-width: 1280px) {
  .visitor-cta-banner {
    padding: 11px 32px;
  }

  .visitor-cta-content {
    gap: 32px;
  }

  .visitor-cta-text {
    font-size: 18px;
  }

  .visitor-cta-button {
    padding: 12px 36px;
    font-size: 15px;
    border-radius: 28px;
  }
}

/* ============================================
   Assortment Request Floating CTA
   ============================================ */
.assortment-request-fab {
  position: fixed;
  bottom: 24px;
  left: 90px;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: var(--color-success);
  color: white;
  border: none;
  border-radius: var(--border-radius-full);
  cursor: pointer;
  box-shadow:
    0 4px 12px rgba(16, 185, 129, 0.35),
    0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all var(--transition-fast);
}

.assortment-request-fab:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow:
    0 6px 20px rgba(16, 185, 129, 0.45),
    0 4px 8px rgba(0, 0, 0, 0.15);
}

.assortment-request-fab:active {
  transform: translateY(0);
}

.fab-count {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: var(--color-success);
  border: 2px solid var(--color-success);
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
}

/* Transition */
.fade-up-enter-active {
  transition: all 0.3s ease-out;
}
.fade-up-leave-active {
  transition: all 0.2s ease-in;
}
.fade-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@media (max-width: 1023px) {
  .catalog-page {
    height: calc(100vh - var(--header-height) - var(--mobile-footer-height));
  }

  .assortment-request-fab {
    display: none;
  }

  .products-container {
    padding-bottom: var(--spacing-md);
  }
}
</style>
